import { TOPIC_BOOKMARK_SVG } from '../../../shared/topicReactionMarkup';
import { LIST_SVG, PLAY_ICON_INNER } from '../../../shared/playerIcons';

/**
 * Player bar + full-screen video player markup. The previous markup
 * burned in a fake "Ch.2: Tokens and Embeddings / How LLMs
 * Actually Work / 🧠 / Chapter 1 of 6" set of initial values, and
 * hardcoded "Upgrade — from $4.99/mo" + "Video mode is a Pro & Max
 * feature" marketing copy.
 *
 * Now every value is data-bind. The actual paint happens in
 * playerUiController when a chapter starts playing. Until then the
 * placeholders stay neutral ("—" / "Pick a chapter" / etc.).
 */
export const playerMarkup = String.raw`<!-- Global Player Bar. The visible class is added by
     playerUiController.showPlayer() when a chapter starts. Keeping
     it off in the initial markup prevents the bar from sitting on
     top of chapter lists on the topic detail page before the user
     has chosen a chapter. -->
<div class="web-player" id="webPlayer">
  <div class="wp-mini">
    <button class="wp-info" type="button" aria-label="Open current chapter">
      <span class="wp-thumb-progress" id="playerThumbProgress" aria-hidden="true">
        <span class="wp-thumb" id="playerThumb" data-bind="player.thumb" aria-hidden="true"></span>
      </span>
      <div class="wp-meta">
        <div class="wp-title" id="playerTitle" data-bind="player.chapterTitle">Pick a chapter to start</div>
        <div class="wp-secondary"><div class="wp-sub" id="playerSub"><span id="playerTopicName">—</span><span id="playerTrace" hidden></span></div></div>
      </div>
    </button>
    <button class="wp-mini-favorite" id="miniFavoriteBtn" type="button" data-proto-click="toggleStar(this)" aria-label="Save current topic" disabled>
      ${TOPIC_BOOKMARK_SVG}
    </button>
    <button class="wp-btn play wp-mini-play" id="miniPlayBtn" data-proto-click="togglePlay()" aria-label="Play or pause audio">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" id="miniPlayIcon">${PLAY_ICON_INNER}</svg>
    </button>
    <button class="wp-mini-watch" type="button" data-proto-click="triggerVideoPaywall()" aria-label="Watch current chapter">
      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="6" width="13" height="12" rx="2"></rect><path d="m16 10 5-3v10l-5-3z"></path></svg>
    </button>
    <button class="wp-mini-playlist" type="button" data-open-now-playing="true" aria-label="Open now playing list">
      ${LIST_SVG}
    </button>
  </div>
  <section class="wp-now-playing" aria-labelledby="nowPlayingTitle" tabindex="-1">
    <div class="wp-now-playing-gesture">
      <span class="wp-now-playing-handle" aria-hidden="true"></span>
      <button class="wp-now-playing-close" type="button" aria-label="Close now playing">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"></path></svg>
      </button>
    </div>
    <h2 id="nowPlayingTitle" class="wp-now-playing-title">Now playing</h2>
    <div class="wp-now-playing-scroll">
      <section class="wp-now-playing-hero" id="nowPlayingTopicCard" aria-label="Current topic"></section>
      <div class="wp-playlist" id="nowPlayingPlaylist" role="list" aria-label="Chapters in this topic"></div>
    </div>
    <div class="wp-controls">
      <div class="wp-transport">
        <span class="wp-time" id="audioTime">0:00 / 0:00</span>
        <button class="wp-btn" data-audio-control="previous" aria-label="Previous audio chapter">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="11 19 2 12 11 5 11 19"></polygon><polygon points="22 19 13 12 22 5 22 19"></polygon></svg>
        </button>
        <button class="wp-btn wp-seek-btn" data-proto-click="seekAudioRelative(-10)" aria-label="Back 10 seconds"><svg viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true"><path d="M480-80q-75 0-140.5-28.5t-114-77q-48.5-48.5-77-114T120-440h80q0 117 81.5 198.5T480-160q117 0 198.5-81.5T760-440q0-117-81.5-198.5T480-720h-6l62 62-56 58-160-160 160-160 56 58-62 62h6q75 0 140.5 28.5t114 77q48.5 48.5 77 114T840-440q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T480-80ZM360-320v-180h-60v-60h120v240h-60Zm140 0q-17 0-28.5-11.5T460-360v-160q0-17 11.5-28.5T500-560h80q17 0 28.5 11.5T620-520v160q0 17-11.5 28.5T580-320h-80Zm20-60h40v-120h-40v120Z"/></svg></button>
        <button class="wp-btn play" id="playBtn" data-proto-click="togglePlay()" aria-label="Play or pause audio">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" id="playIcon">${PLAY_ICON_INNER}</svg>
        </button>
        <button class="wp-btn wp-seek-btn" data-proto-click="seekAudioRelative(10)" aria-label="Forward 10 seconds"><svg viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true"><path d="M360-320v-180h-60v-60h120v240h-60Zm140 0q-17 0-28.5-11.5T460-360v-160q0-17 11.5-28.5T500-560h80q17 0 28.5 11.5T620-520v160q0 17-11.5 28.5T580-320h-80Zm20-60h40v-120h-40v120ZM480-80q-75 0-140.5-28.5t-114-77q-48.5-48.5-77-114T120-440q0-75 28.5-140.5t77-114q48.5-48.5 114-77T480-800h6l-62-62 56-58 160 160-160 160-56-58 62-62h-6q-117 0-198.5 81.5T200-440q0 117 81.5 198.5T480-160q117 0 198.5-81.5T760-440h80q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T480-80Z"/></svg></button>
        <button class="wp-btn" data-audio-control="next" aria-label="Next audio chapter">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="13 5 22 12 13 19 13 5"></polygon><polygon points="2 5 11 12 2 19 2 5"></polygon></svg>
        </button>
      </div>
      <div class="wp-progress">
        <div class="wp-progress-fill" id="playerProgress"></div>
        <input class="wp-progress-slider" id="audioProgressSlider" type="range" min="0" max="100" step="0.1" value="0" aria-label="Audio playback position" aria-valuetext="0:00 of 0:00">
      </div>
      <div class="wp-now-playing-options">
        <button class="wp-btn wp-speed-btn" id="audioSpeedBtn" data-proto-click="cycleAudioSpeed()" aria-label="Change audio speed"><span>1x</span></button>
        <div class="wp-mode">
          <button class="wp-mode-btn wp-mode-watch" type="button" data-proto-click="triggerVideoPaywall()" aria-label="Watch current chapter video"><svg class="wp-mode-watch-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="6" width="13" height="12" rx="2"></rect><path d="m16 10 5-3v10l-5-3z"></path></svg></button>
        </div>
      </div>
    </div>
  </section>
</div>

<!-- TikTok-Style Full-Screen Video Player -->
<div class="video-fullscreen" id="videoFullscreen">
  <div class="vf-chapters" id="vfChapters">
    <!-- Chapters are rendered dynamically by JS -->
  </div>
  <div class="vf-loading-overlay" id="vfLoadingOverlay" role="status" aria-live="polite" aria-label="Loading next chapter" aria-hidden="true">
    <div class="vf-loading-brand" aria-hidden="true">
      <img src="/assets/brand/deepmox-wordmark-dark-320-v2.png" alt="">
    </div>
  </div>
  <div class="vf-readiness-overlay" id="vfReadinessOverlay" aria-hidden="true">
    <div class="vf-readiness-card">
      <strong id="vfReadinessTitle">This chapter video is still being prepared</strong>
      <span>You can continue reading now and try the video again later.</span>
      <div class="vf-readiness-actions">
        <button type="button" class="vf-readiness-secondary" id="vfContinueReadingBtn">Continue reading</button>
      </div>
    </div>
  </div>
  <div class="vf-playback-retry" id="vfPlaybackRetry" role="status" aria-live="polite" hidden>
    <div class="vf-playback-retry-card">
      <strong>Video is ready</strong>
      <span>Your browser needs one more tap to start playback.</span>
      <button type="button" class="vf-playback-retry-action" data-proto-click="toggleVideoPlay()">Tap to play</button>
    </div>
  </div>

  <div class="vf-top">
    <button class="vf-back" id="vfBackBtn" data-proto-click="closeVideoFullscreen()">
      <span class="vf-back-symbol">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M19 12H5"></path><path d="M12 19l-7-7 7-7"></path></svg>
      </span>
      <span>Back</span>
    </button>
    <div class="vf-chapter-info">
      <div class="vf-topic-title" id="vfTopicName" data-bind="player.vfTopicName">DeepMox</div>
      <div class="vf-chapter-label" id="vfChapterLabel" data-bind="player.vfChapterLabel">Loading chapter details</div>
    </div>
  </div>

  <div class="vf-actions" id="vfActions">
    <div class="vf-chapter-dots" id="vfChapterDots" aria-label="Video chapters"></div>
  </div>

  <div class="vf-corner-actions" id="vfCornerActions">
    <button class="vf-action-btn" id="vfShareBtn" data-proto-click="shareVfChapter()" aria-label="Share">
      <div class="vf-action-icon">
        <svg viewBox="0 0 24 24"><circle cx="18" cy="5" r="3" fill="#fff"></circle><circle cx="6" cy="12" r="3" fill="#fff"></circle><circle cx="18" cy="19" r="3" fill="#fff"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" stroke="#fff" stroke-width="1.5" fill="none"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" stroke="#fff" stroke-width="1.5" fill="none"></line></svg>
      </div>
    </button>
    <button class="vf-action-btn" id="vfAudioBtn" data-proto-click="switchVideoToAudio()" aria-label="Listen">
      <div class="vf-action-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"></path></svg>
      </div>
    </button>
    <button class="vf-action-btn" id="vfCloseBtn" data-proto-click="stopVideoFullscreen()" aria-label="Close">
      <div class="vf-action-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
      </div>
    </button>
  </div>

  <div class="vf-center-controls" id="vfCenterControls" aria-label="Video controls">
    <button class="vf-media-btn" id="vfPrevBtn" data-proto-click="previousVideoChapter()" aria-label="Previous chapter">
      <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="11 19 2 12 11 5 11 19"></polygon><polygon points="22 19 13 12 22 5 22 19"></polygon></svg>
    </button>
    <button class="vf-media-btn vf-seek-btn" id="vfBack10Btn" data-proto-click="seekVideoRelative(-10)" aria-label="Back 10 seconds">
      <svg viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true"><path d="M480-80q-75 0-140.5-28.5t-114-77q-48.5-48.5-77-114T120-440h80q0 117 81.5 198.5T480-160q117 0 198.5-81.5T760-440q0-117-81.5-198.5T480-720h-6l62 62-56 58-160-160 160-160 56 58-62 62h6q75 0 140.5 28.5t114 77q48.5 48.5 77 114T840-440q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T480-80ZM360-320v-180h-60v-60h120v240h-60Zm140 0q-17 0-28.5-11.5T460-360v-160q0-17 11.5-28.5T500-560h80q17 0 28.5 11.5T620-520v160q0 17-11.5 28.5T580-320h-80Zm20-60h40v-120h-40v120Z"/></svg>
    </button>
    <button class="vf-media-btn primary" id="vfPlayToggleBtn" data-proto-click="toggleVideoPlay()" aria-label="Pause or play">
      <svg id="vfPlayToggleIcon" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
    </button>
    <button class="vf-media-btn vf-seek-btn" id="vfForward10Btn" data-proto-click="seekVideoRelative(10)" aria-label="Forward 10 seconds">
      <svg viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true"><path d="M360-320v-180h-60v-60h120v240h-60Zm140 0q-17 0-28.5-11.5T460-360v-160q0-17 11.5-28.5T500-560h80q17 0 28.5 11.5T620-520v160q0 17-11.5 28.5T580-320h-80Zm20-60h40v-120h-40v120ZM480-80q-75 0-140.5-28.5t-114-77q-48.5-48.5-77-114T120-440q0-75 28.5-140.5t77-114q48.5-48.5 114-77T480-800h6l-62-62 56-58 160 160-160 160-56-58 62-62h-6q-117 0-198.5 81.5T200-440q0 117 81.5 198.5T480-160q117 0 198.5-81.5T760-440h80q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T480-80Z"/></svg>
    </button>
    <button class="vf-media-btn" id="vfNextBtn" data-proto-click="nextVideoChapter()" aria-label="Next chapter">
      <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="13 5 22 12 13 19 13 5"></polygon><polygon points="2 5 11 12 2 19 2 5"></polygon></svg>
    </button>
  </div>

  <div class="vf-bottom">
    <div class="vf-bottom-title" id="vfChapterTitle" data-bind="player.vfChapterTitle">Chapter</div>
    <div class="vf-chapter-desc" id="vfChapterDesc" data-bind="player.vfChapterDesc">Chapter context will appear here.</div>
    <div class="vf-progress-shell" id="vfProgress" aria-label="Video progress">
      <div class="vf-progress-fill" id="vfProgressFill"></div>
    </div>
    <div class="vf-controls">
      <span class="vf-time" id="vfTime">0:00</span>
      <div class="vf-control-actions">
        <button class="vf-control-btn" id="vfSpeedBtn" data-proto-click="cycleVideoSpeed()">1x</button>
        <button class="vf-control-btn" id="vfPipBtn" data-proto-click="toggleVideoPip()" hidden aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="5" width="18" height="14" rx="2"></rect><rect x="13" y="12" width="6" height="4" rx="1"></rect></svg>
          PiP
        </button>
        <button class="vf-control-btn" id="vfFullscreenBtn" data-proto-click="toggleVideoFullscreen()" aria-label="Toggle fullscreen" hidden aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 3H5a2 2 0 0 0-2 2v3"></path><path d="M16 3h3a2 2 0 0 1 2 2v3"></path><path d="M8 21H5a2 2 0 0 1-2-2v-3"></path><path d="M16 21h3a2 2 0 0 0 2-2v-3"></path></svg>
        </button>
      </div>
    </div>
  </div>

  <button class="vf-replay" id="vfReplay" data-proto-click="replayVideoCourse()" aria-label="Replay topic">
    <span class="vf-replay-progress" aria-hidden="true">
      <span class="vf-replay-play">
        <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="8 5 19 12 8 19 8 5"></polygon></svg>
      </span>
    </span>
    <span class="vf-replay-copy">
      <strong>Replay topic</strong>
      <span>Tap to restart from Chapter 1</span>
    </span>
  </button>
</div>

`;
